/**
 * Project Messages sub-page — messages for the project selected in the context
 * bar. List ⇄ detail, matching the previous in-project tab behavior.
 */

import { useState } from 'react';
import { Stack, useTheme } from '@fti/design-system';
import {
  useMessages,
  useCreateMessage,
  useUpdateMessage,
  useRegisterMessage,
  useDuplicateMessage,
} from '../../../api/media-sim/messages';
import { MessageList } from '../../../components/media-sim/messages/MessageList';
import { MessageDetail } from '../../../components/media-sim/messages/MessageDetail';
import { ProjectScopeGate } from './ProjectScopeGate';
import type { Project } from '../../../types/media-sim';

function MessagesContent({ project }: { project: Project }) {
  const { theme } = useTheme();
  const projectId = project.id;

  const { data: messages, refetch: refetchMessages } = useMessages(projectId);
  const { refetch: refetchRegisteredMessages } = useMessages(projectId, { status: 'registered' });
  const { createMessage } = useCreateMessage();
  const { updateMessage } = useUpdateMessage();
  const { registerMessage } = useRegisterMessage();
  const { duplicateMessage } = useDuplicateMessage();
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const selectedMessage = messages?.find((m) => m.id === selectedMessageId);

  const handleCreateMessage = async () => {
    try {
      const msg = await createMessage(projectId, { message_name: 'New Message', body: '' });
      if (msg) { refetchMessages(); setSelectedMessageId(msg.id); }
    } catch { /* handled by error state */ }
  };

  const handleUpdateMessage = async (data: { message_name?: string; body?: string }) => {
    if (!selectedMessageId) return;
    try { await updateMessage(projectId, selectedMessageId, data); } catch { /* silent auto-save failure */ }
  };

  const handleRegisterMessage = async (msgId: string) => {
    try {
      await registerMessage(projectId, msgId);
      refetchMessages();
      refetchRegisteredMessages();
      if (selectedMessageId === msgId) setSelectedMessageId(null);
    } catch { /* handled by error state */ }
  };

  const handleDuplicateMessage = async (msgId: string) => {
    try { await duplicateMessage(projectId, msgId); refetchMessages(); } catch { /* handled by error state */ }
  };

  return (
    <Stack gap="md">
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
        Messages — {project.project_name}
      </h1>
      {selectedMessage ? (
        <MessageDetail
          message={selectedMessage}
          onUpdate={handleUpdateMessage}
          onRegister={() => handleRegisterMessage(selectedMessage.id)}
          onDuplicate={() => handleDuplicateMessage(selectedMessage.id)}
          onBack={() => { setSelectedMessageId(null); refetchMessages(); }}
        />
      ) : (
        <MessageList
          messages={messages || []}
          onSelect={setSelectedMessageId}
          onRegister={handleRegisterMessage}
          onDuplicate={handleDuplicateMessage}
          onCreate={handleCreateMessage}
        />
      )}
    </Stack>
  );
}

export function ProjectMessagesPage() {
  return <ProjectScopeGate>{(project) => <MessagesContent project={project} />}</ProjectScopeGate>;
}
